<script setup>
import { computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import FullPageModal from '@/js/core/components/parts/FullPageModal.vue';
import NavigationPanel from '@/js/core/components/parts/navigation-panel/NavigationPanel.vue';
import CourseModalMetaList from '@/js/core/components/pages/course-modal/CourseModalMetaList.vue';
import CourseModalCtaSidebar from '@/js/core/components/pages/course-modal/CourseModalCtaSidebar.vue';
import PersonProfileModal from '@/js/core/components/pages/PersonProfileModal.vue';
import { usePersonPanel } from '@/js/core/composables/usePersonPanel';
import { useModalClose } from '@/js/core/composables/useModalClose';
import { useNotFoundRedirect } from '@/js/core/composables/useNotFoundRedirect';
import { useCoursesStore } from '@/js/core/stores/courses-store';
import { useTimezoneStore } from '@/js/core/stores/timezone-store';
import { normalizeCourse } from '@/js/core/utils/courses';

const route = useRoute();
const coursesStore = useCoursesStore();
const timezoneStore = useTimezoneStore();
const { closeModal } = useModalClose({ fallbackRoute: { name: 'courses' } });
const { redirectOnNotFound } = useNotFoundRedirect();

onMounted(() => {
  timezoneStore.hydrateFromCurrentProfile();
});

const courseSlug = computed(() => route.params.courseSlug || '');

watch(
  () => coursesStore.courseErrorDetails,
  error => {
    if (error) redirectOnNotFound(error);
  },
  { immediate: true }
);

const course = computed(() => {
  const rawCourse = coursesStore.courseBySlug(String(courseSlug.value));

  return rawCourse ? normalizeCourse(rawCourse, timezoneStore.effectiveTimezone) : null;
});

const {
  selectedProfileUuid,
  panelId: personPanelId,
  openPersonPanel,
  closePersonPanel,
} = usePersonPanel();

const descriptionHtml = computed(() => course.value?.description || '');
</script>

<template>
  <FullPageModal
    v-if="course || coursesStore.courseError"
    :title="course?.name || 'Course'"
    wrapper-class="is-course-modal"
    @close="closeModal"
  >
    <template #header-top>
      <div class="course-modal__meta">
        <span class="course-modal__category">Courses</span>
      </div>
    </template>

    <div v-if="coursesStore.courseError" class="no-results">
      {{ coursesStore.courseError }}
    </div>

    <div v-else-if="course" class="course-modal__container">
      <CourseModalMetaList
        :course="course"
        :active-profile-uuid="selectedProfileUuid"
        :person-panel-id="personPanelId"
        @person-click="openPersonPanel"
      />
      <CourseModalCtaSidebar :links="course.links" />
      <div v-if="descriptionHtml" class="course-modal__description">
        <!-- Course descriptions are sanitized on the plugin write path with wp_kses_post. -->
        <div class="course-modal__description-wrapper" v-html="descriptionHtml"></div>
      </div>
    </div>

    <template #footer>
      <div class="view-course-navbar__wrapper">
        <NavigationPanel />
      </div>
    </template>

    <template #overlay>
      <PersonProfileModal
        v-if="selectedProfileUuid"
        :key="selectedProfileUuid"
        :profile-uuid="selectedProfileUuid"
        :panel-id="personPanelId"
        @close="closePersonPanel"
      />
    </template>
  </FullPageModal>
</template>
